import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { ShopSettingsService } from '../../general/services/shop-settings.service';
import { Order } from '../../order/models/order';
import { OrderLineType } from '../../order/models/order-line-type';
import { OrderTypeEnum } from '../../order/models/order-type.enum';
import { Product } from '../../order/models/product';
import { OrderStateService } from '../../order/services/order-state.service';
import { ProductsStateService } from '../../order/services/products-state.service';

@Injectable()
export class RefundCardOrderService {

  constructor(
    private shopSettingsService: ShopSettingsService,
    private productsStateService: ProductsStateService,
    private orderStateService: OrderStateService,
  ) {
  }

  get guestCardProduct(): Observable<Product> {
    const guestCardProductId = this.shopSettingsService.shopSettings.guestCardProduct;
    return this.productsStateService.depositProducts.pipe(
      map(products => {
        if (!products || !guestCardProductId) {
          return null;
        }
        return products.find(product => product.id === guestCardProductId);
      }),
    );
  }

  get guestCardProductPrice(): Observable<number> {
    return this.guestCardProduct.pipe(
      map((guestCardProduct: Product) => {
        return guestCardProduct ? guestCardProduct.price : 0;
      }),
    );
  }

  get guestCardRefundProduct(): Observable<Product> {
    return this.productsStateService.giftcardProducts.pipe(
      map(allGiftCards => {
        return allGiftCards ? allGiftCards.find(card => card.price === 1) : null;
      }),
    );
  }

  createOrder(refundAmount: number): Observable<Order> {
    return this.guestCardRefundProduct.pipe(
      switchMap(guestCardRefundProduct => {
        return this.guestCardProduct.pipe(
          switchMap(guestCardProduct => {
            return this.orderStateService.initialize(OrderTypeEnum.Refund).pipe(
              tap(_ => { this._fillOrder(guestCardRefundProduct, refundAmount, guestCardProduct); }),
            );
          }),
        );
      }),
      switchMap(_ => this.orderStateService.createOrder()),
      tap(order => { this.orderStateService.sessionOrderId = order ? order.id : 0; }),
    );
  }

  private _fillOrder(guestCardRefundProduct: Product, refundAmount: number, guestCardProduct: Product): void {
    const order = this.orderStateService.order;
    order.addOrderLine(
      OrderLineType.SingleProduct,
      guestCardRefundProduct.name,
      guestCardRefundProduct.id,
      -(refundAmount - guestCardProduct.price),
      guestCardRefundProduct.taxRate,
      1
    );

    order.addOrderLine(
      OrderLineType.SingleProduct,
      guestCardProduct.name,
      guestCardProduct.id,
      -guestCardProduct.price,
      guestCardProduct.taxRate,
      1
    );
  }
}
